import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Container, Typography, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, InputAdornment,
  CircularProgress, Grid, Card, CardContent, Snackbar, Alert, Chip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Key as KeyIcon,
  Search as SearchIcon, Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon, Stars as StarsIcon, History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import api from '../api/api';
import ClientePacienteModal from './ClientePacienteModal';
import BusquedaCamaraPaciente from './BusquedaCamaraPaciente';
import { useUserData } from '../hooks/useUserData';
import RecordatorioBackup from './RecordatorioBackup';
import PuntosCuentaModal from './PuntosCuentaModal';
import PuntosMovimientosModal from './PuntosMovimientosModal';

// --- CONSTANTES DE ESTILO ---
const drawerWidth = 0;
const PRIMARY_DARK_BG = '#1e293b';
const PRIMARY_COLOR = '#4f46e5';
const SUCCESS_COLOR = '#10b981';

// --- COMPONENTES AUXILIARES ---

const ClienteCard = React.memo(({ cliente, nombreSucursal, esNuevo, onEdit, onDelete, onNavigate, onToken, onPuntos, onMovimientos }) => {
  const tieneToken = !!cliente.token_cliente;

  // Generar iniciales para el avatar
  const iniciales = cliente.nombre_cliente
    ? cliente.nombre_cliente.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    : '??';

  // Esquemas de colores dinámicos según el ID
  const colorSchemes = [
    { bg: '#eeedfe', text: '#3c3489', border: '#4f46e5' }, // Indigo
    { bg: '#e1f5ee', text: '#085041', border: '#10b981' }, // Verde
    { bg: '#faeeda', text: '#633806', border: '#f59e0b' }  // Ámbar
  ];
  const scheme = colorSchemes[cliente.id_cliente % 3];

  return (
    <Card sx={{ 
      borderRadius: '12px', 
      border: '0.5px solid #e2e8f0',
      borderLeft: `3px solid ${scheme.border}`,
      background: '#fcfcfc',
      transition: 'all 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': { 
        boxShadow: '0 4px 16px rgba(79,70,229,0.10)', 
        borderColor: scheme.border 
      }
    }}>
      <CardContent sx={{ p: '16px 16px 12px !important' }}>
        {/* Header: Avatar, Nombre y ID */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '12px' }}>
          <Box sx={{ 
            width: 40, height: 40, borderRadius: '50%', 
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', fontWeight: 500, 
            bgcolor: scheme.bg, color: scheme.text 
          }}>
            {iniciales}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {cliente.nombre_cliente}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>ID: {cliente.id_cliente}</Typography>
          </Box>
          {esNuevo && (
            <Chip label="Nuevo" size="small" sx={{ height: '20px', fontSize: '11px', fontWeight: 600, bgcolor: SUCCESS_COLOR, color: '#ffffff' }} />
          )}
        </Box>

        <Box sx={{ height: '0.5px', bgcolor: '#e2e8f0', mb: '12px' }} />

        {/* Info Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', mb: '12px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.03em' }}>Email</Typography>
            <Typography sx={{ fontSize: '13px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {cliente.email_cliente || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.03em' }}>Teléfono</Typography>
            <Typography sx={{ fontSize: '13px', color: '#1e293b' }}>
              {cliente.telefonos_cliente || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
            <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>Sucursal</Typography>
            <Box component="span" sx={{ 
              display: 'inline-flex', px: 1, py: 0.2, borderRadius: '99px', fontSize: '12px', fontWeight: 500,
              bgcolor: scheme.bg, color: scheme.text, border: `0.5px solid ${scheme.border}40`
            }}>
              {nombreSucursal}
            </Box>
          </Box>
        </Box>

        <Box sx={{ height: '0.5px', bgcolor: '#e2e8f0', mb: '8px' }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => onNavigate(cliente.id_cliente)} sx={{ color: '#64748b', '&:hover': { color: PRIMARY_COLOR, bgcolor: '#f1f5f9' } }}>
            <VisibilityIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onEdit(cliente)} sx={{ color: '#64748b', '&:hover': { color: '#d97706', bgcolor: '#f1f5f9' } }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(cliente)} sx={{ color: '#64748b', '&:hover': { color: '#ef4444', bgcolor: '#f1f5f9' } }}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Botón de Puntos de Fidelización (cuenta) */}
          <IconButton size="small" onClick={() => onPuntos(cliente)} sx={{ color: '#f59e0b', '&:hover': { bgcolor: '#fef3c7' } }} title="Cuenta de puntos">
            <StarsIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Botón de Movimientos de Puntos */}
          <IconButton size="small" onClick={() => onMovimientos(cliente)} sx={{ color: '#4f46e5', '&:hover': { bgcolor: '#eeedfe' } }} title="Movimientos de puntos">
            <HistoryIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={() => !tieneToken && onToken(cliente.id_cliente)}
            disabled={tieneToken}
            sx={{ 
              color: tieneToken ? SUCCESS_COLOR : '#64748b',
              '&:hover': { color: tieneToken ? SUCCESS_COLOR : PRIMARY_COLOR }
            }}
          >
            {tieneToken ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <KeyIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
});

const ClienteModal = ({ open, onClose, selectedCliente, onSave, sucursales, userSucursales }) => {
  const [formData, setFormData] = useState({ nombre_cliente: '', email_cliente: '', telefonos_cliente: '', id_sucursal: '' });
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (open) {
      setFormData({
        nombre_cliente: selectedCliente?.nombre_cliente || '',
        email_cliente: selectedCliente?.email_cliente || '',
        telefonos_cliente: selectedCliente?.telefonos_cliente || '',
        id_sucursal: selectedCliente?.id_sucursal || (userSucursales[0] || ''),
      });
      setEmailError('');
    }
  }, [open, selectedCliente, userSucursales]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) { setEmailError('Formato inválido'); return false; }
    setEmailError(''); return true;
  };

  const isSaveDisabled = !formData.nombre_cliente || !formData.id_sucursal || !!emailError;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{selectedCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
      <DialogContent dividers>
        <TextField label="Nombre" fullWidth margin="dense" value={formData.nombre_cliente} onChange={(e) => setFormData({...formData, nombre_cliente: e.target.value})} error={!formData.nombre_cliente} />
        <TextField label="Email" fullWidth margin="dense" value={formData.email_cliente} onChange={(e) => { setFormData({...formData, email_cliente: e.target.value}); validateEmail(e.target.value); }} error={!!emailError} helperText={emailError} />
        <TextField label="Teléfonos" fullWidth margin="dense" value={formData.telefonos_cliente} onChange={(e) => setFormData({...formData, telefonos_cliente: e.target.value})} />
        <TextField select label="Sucursal" fullWidth margin="dense" value={formData.id_sucursal} onChange={(e) => setFormData({...formData, id_sucursal: e.target.value})}>
          {sucursales.map(s => <MenuItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre_sucursal}</MenuItem>)}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSave(formData)} disabled={isSaveDisabled} sx={{ bgcolor: PRIMARY_COLOR }}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- COMPONENTE PRINCIPAL ---

const Clientes = ({ openSidebar, setOpenSidebar }) => {
  const navigate = useNavigate();
  const userData = useUserData();

  // Estados de Datos
  const [clientes, setClientes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [razas, setRazas] = useState([]);
  const [especies, setEspecies] = useState([]);
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 21;
  const [isDataReady, setIsDataReady] = useState(false);

  // Modales
  const [openModal, setOpenModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [openPacienteModal, setOpenPacienteModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');
  const [openCamara, setOpenCamara] = useState(false);
  const [camaraKey, setCamaraKey] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados para el Modal de Puntos (cuenta)
  const [puntosModalOpen, setPuntosModalOpen] = useState(false);
  const [clientePuntos, setClientePuntos] = useState(null);

  // Estados para el Modal de Movimientos de Puntos
  const [movimientosModalOpen, setMovimientosModalOpen] = useState(false);
  const [clienteMovimientos, setClienteMovimientos] = useState(null);

  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  // Abrir modal de puntos (cuenta) desde la tarjeta
  const handleOpenPuntos = (cliente) => {
    setClientePuntos(cliente);
    setPuntosModalOpen(true);
  };

  // Abrir modal de movimientos de puntos desde la tarjeta
  const handleOpenMovimientos = (cliente) => {
    setClienteMovimientos(cliente);
    setMovimientosModalOpen(true);
  };

  // Función de Carga
  const fetchClientes = useCallback(async (search = '') => {
    if (!userData.id_pet) return;
    try {
      const term = search.trim();
      const url = term ? `/clientes/buscar?terminos_busqueda=${encodeURIComponent(term)}` : '/clientes/';
      const response = await api.get(url);
      let data = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
      
      const filtered = data
        .filter(c => userData.sucursales.includes(c.id_sucursal) && c.habilitado !== false)
        .sort((a, b) => {
          const esClienteContadoA = a.nombre_cliente?.trim().toLowerCase() === 'cliente contado';
          const esClienteContadoB = b.nombre_cliente?.trim().toLowerCase() === 'cliente contado';

          if (esClienteContadoA && !esClienteContadoB) return -1;
          if (!esClienteContadoA && esClienteContadoB) return 1;

          return b.id_cliente - a.id_cliente;
        });
      
      setClientes(filtered);
      setPage(0);
    } catch (err) {
      if (err.response?.status !== 404) showSnackbar('Error al obtener clientes', 'error');
      else setClientes([]);
    } finally { setLoading(false); }
  }, [userData]);

  // Carga Inicial
  useEffect(() => {
    const loadInitialData = async () => {
      if (!userData.id_pet) return;
      setLoading(true);
      try {
        const [sucRes, razRes, espRes] = await Promise.all([
          api.get('/sucursales/').catch(() => ({ data: [] })),
          api.get('/razas/').catch(() => ({ data: [] })),
          api.get('/especies/').catch(() => ({ data: [] }))
        ]);
        setSucursales(sucRes.data.filter(s => userData.sucursales.includes(s.id_sucursal)));
        setRazas(razRes.data.filter(r => r.id_pet === userData.id_pet));
        setEspecies(espRes.data.filter(e => e.id_pet === userData.id_pet));
        await fetchClientes(searchTerm);
        setIsDataReady(true);
      } catch (err) { showSnackbar('Error cargando catálogos', 'error'); }
    };
    loadInitialData();
  }, [userData, fetchClientes]);

  // Debounce Búsqueda
  useEffect(() => {
    if (!userData.id_pet) return;

    if (searchTerm.trim() === '') return;
    const timer = setTimeout(() => fetchClientes(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchClientes, userData.id_pet]);

  // Memorizaciones
  const sucursalesMap = useMemo(() => {
    return sucursales.reduce((acc, s) => ({ ...acc, [s.id_sucursal]: s.nombre_sucursal }), {});
  }, [sucursales]);

  const nuevosIds = useMemo(() => new Set(clientes.slice(0, 5).map(c => c.id_cliente)), [clientes]);

  const paginatedClientes = useMemo(() => {
    return clientes.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [clientes, page]);

  // Handlers
  const handleSave = async (formData) => {
    try {
      const payload = { ...formData, id_sucursal: Number(formData.id_sucursal) };
      if (selectedCliente) {
        await api.put(`/clientes/${selectedCliente.id_cliente}`, payload);
        showSnackbar('Cliente actualizado');
      } else {
        await api.post('/clientes/', payload);
        showSnackbar('Cliente creado');
      }
      setOpenModal(false);
      fetchClientes(searchTerm);
    } catch (err) { showSnackbar(err.response?.data?.detail || 'Error al guardar', 'error'); }
  };

  const handleExecuteDelete = async () => {
    try {
      await api.delete(`/clientes/${clienteToDelete.id_cliente}`, { params: { motivo: motivoEliminacion } });
      setClientes(prev => prev.filter(c => c.id_cliente !== clienteToDelete.id_cliente));
      showSnackbar('Cliente archivado');
      setDeleteDialogOpen(false);
    } catch (err) { showSnackbar('Error al eliminar', 'error'); }
  };

  const handleGenerateToken = async (id) => {
    try {
      await api.post(`/clientes/${id}/generar-token`);
      showSnackbar('Token generado');
      fetchClientes(searchTerm);
    } catch (err) { showSnackbar('Error al generar token', 'error'); }
  };

  if (loading && !isDataReady) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', minHeight: '100vh' }}>
      <Sidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} userRole={userData?.id_rol} /> 
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            md: openSidebar ? `calc(100% - ${drawerWidth}px)` : '100%',
          },
          ml: {
            md: openSidebar ? `${drawerWidth}px` : -30,
          },
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Container maxWidth="xl" sx={{ pt: 10 }}>
          <RecordatorioBackup />
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2, mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: PRIMARY_DARK_BG }}>Clientes</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedCliente(null); setOpenModal(true); }} sx={{ bgcolor: PRIMARY_COLOR, textTransform: 'none', borderRadius: '8px' }}>Nuevo</Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenPacienteModal(true)} sx={{ textTransform: 'none', borderRadius: '8px' }}>Cliente + Paciente</Button>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={() => setOpenCamara(true)} sx={{ bgcolor: SUCCESS_COLOR, textTransform: 'none', borderRadius: '8px' }}>QR</Button>
            </Box>
          </Box>

          <TextField 
            fullWidth 
            placeholder="Buscar por nombre cliente..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            sx={{ mb: 4, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} 
          />

          {clientes.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {paginatedClientes.map(cliente => (
                  <Grid item xs={12} sm={6} md={6} lg={4} xl={4} key={cliente.id_cliente}>
                    <ClienteCard 
                      cliente={cliente} 
                      nombreSucursal={sucursalesMap[cliente.id_sucursal]} 
                      esNuevo={nuevosIds.has(cliente.id_cliente)}
                      onEdit={(c) => { setSelectedCliente(c); setOpenModal(true); }}
                      onDelete={(c) => { setClienteToDelete(c); setDeleteDialogOpen(true); }}
                      onNavigate={(id) => navigate(`/clientes/${id}`)}
                      onToken={handleGenerateToken}
                      onPuntos={handleOpenPuntos}
                      onMovimientos={handleOpenMovimientos}
                    />
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2, pb: 4 }}>
                <Button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                <Typography sx={{ alignSelf: 'center', fontSize: '14px', color: '#64748b' }}>
                  Página {page + 1} de {Math.ceil(clientes.length / rowsPerPage)}
                </Typography>
                <Button disabled={page >= Math.ceil(clientes.length / rowsPerPage) - 1} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
              </Box>
            </>
          ) : (
            <Box sx={{ mt: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">No se encontraron clientes.</Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* MODALES */}
      <ClienteModal open={openModal} onClose={() => setOpenModal(false)} selectedCliente={selectedCliente} onSave={handleSave} sucursales={sucursales} userSucursales={userData.sucursales} />
      <ClientePacienteModal open={openPacienteModal} handleClose={() => setOpenPacienteModal(false)} razas={razas} especies={especies} onSaveSuccess={() => fetchClientes(searchTerm)} />
      <BusquedaCamaraPaciente key={camaraKey} open={openCamara} handleClose={() => setOpenCamara(false)} navigate={navigate} onForceRestart={() => setCamaraKey(k => k + 1)} />

      <PuntosCuentaModal
        open={puntosModalOpen}
        onClose={() => setPuntosModalOpen(false)}
        clienteId={clientePuntos?.id_cliente}
        clienteNombre={clientePuntos?.nombre_cliente}
      />

      <PuntosMovimientosModal
        open={movimientosModalOpen}
        onClose={() => setMovimientosModalOpen(false)}
        clienteId={clienteMovimientos?.id_cliente}
        clienteNombre={clienteMovimientos?.nombre_cliente}
        idSucursal={clienteMovimientos?.id_sucursal}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: '#ef4444' }}>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>¿Eliminar a <strong>{clienteToDelete?.nombre_cliente}</strong>? Esta acción es irreversible.</Typography>
          <TextField fullWidth multiline rows={2} label="Motivo" value={motivoEliminacion} onChange={(e) => setMotivoEliminacion(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleExecuteDelete} variant="contained" disabled={!motivoEliminacion.trim()} sx={{ bgcolor: '#ef4444' }}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Clientes;